import { Injectable, computed } from '@angular/core';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { GroupedListItem } from '@maximizer/core/shared/domain';
import { MenuOptionName } from '@maximizer/core/shared/ui/enums';
import {
  AppointmentPrefill,
  EmailEntryType,
  EntryType,
  NotePrefill,
  TaskPrefill,
} from '@maximizer/outlook/shared/domain';
import {
  EntryStore,
  clientNow,
  nextHalfHour,
} from '@maximizer/outlook/shared/data-access';
import { addMinutes } from 'date-fns';
import { OutlookIqBoostStore } from '../state/iq-boost.store';

const DEFAULT_MEETING_DURATION_MINUTES = 30;

@Injectable()
export class OutlookIqBoostCrmActionService {
  readonly confirmCreateCRMAction = computed(() =>
    this.iqBoostStore.confirmCreateCRMAction(),
  );
  readonly confirmCreateCRMActionFor = computed(() =>
    this.iqBoostStore.confirmCreateCRMActionFor(),
  );
  readonly confirmCreateCRMActionForLoading = computed(() =>
    this.iqBoostStore.confirmCreateCRMActionForLoading(),
  );

  readonly embeddedEntryType = computed<EmailEntryType>(
    () => this.entryStore.type() ?? EntryType.AbEntry,
  );
  readonly embeddedEntryId = computed<string>(
    () => this.entryStore.entry()?.key ?? '',
  );

  readonly notePrefill = computed<NotePrefill | null>(() => {
    const action = this.confirmCreateCRMAction();
    if (action?.action !== MenuOptionName.AddNote) return null;
    return {
      richText: action.description ?? action.title ?? '',
      category: 'AI-Generated',
    };
  });

  readonly taskPrefill = computed<TaskPrefill | null>(() => {
    const action = this.confirmCreateCRMAction();
    if (action?.action !== MenuOptionName.ScheduleToDo) return null;
    return {
      activity: action.title ?? action.displayText ?? '',
      dueDate: new Date(),
    };
  });

  readonly appointmentPrefill = computed<AppointmentPrefill | null>(() => {
    const action = this.confirmCreateCRMAction();
    if (action?.action !== MenuOptionName.ScheduleMeeting) return null;

    const startTime = nextHalfHour(clientNow());
    const endTime = addMinutes(startTime, DEFAULT_MEETING_DURATION_MINUTES);

    const session = this.globalStore.session();
    const entry = this.entryStore.entry();
    const type = this.entryStore.type();
    const attendees: GroupedListItem<string>[] = [
      {
        id: session?.user.key ?? '',
        name: session?.user.name ?? '',
        group: 'user',
        isDisabled: true,
      },
      ...(entry?.key
        ? [
            {
              id: entry.key,
              name: '',
              group: type === EntryType.AbEntry ? 'abentry' : 'lead',
              isDisabled: false,
            },
          ]
        : []),
    ];

    return {
      subject: action.title ?? action.displayText ?? '',
      description: action.description ?? null,
      startTime,
      endTime,
      attendees,
    };
  });

  constructor(
    private readonly iqBoostStore: OutlookIqBoostStore,
    private readonly entryStore: EntryStore,
    private readonly globalStore: GlobalStore,
  ) {}
}
